import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminAlertText,
  AdminRowMenuItem,
  AdminPageTitle,
  AdminRowAction,
  AdminRowActionsButton,
  AdminRowAvatar,
  AdminSearchField,
  AdminSortableTableHeader,
  AdminSupportingText,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  AdminFixedRowActionsMenu,
  MESSAGE_DISPLAY_STATUSES,
  messageThreadStatusBadgeClass,
  normalizeMessageStatus,
  normalizeMessageDeliveryStatus,
  AdminListFilterDropdown,
  AdminRowMenuIcon,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import { DashboardStatusLabel } from '../../../components/common/Dashboard';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import useClickOutside from '../../../Hooks/useClickOutside';
import { useInlineRowActions, ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useInlineRowActions';
import { getMessagesForScenarios } from '../../../data/messageModerationScenarios';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';

/** Message ID | Account | Latest | Threads | Status | Flags | Status | Last Message On | Action */
const MESSAGES_LIST_GRID =
  'minmax(96px, 0.85fr) minmax(110px, 1.1fr) minmax(140px, 1.6fr) minmax(64px, 0.55fr) minmax(88px, 0.75fr) minmax(56px, 0.45fr) minmax(100px, 0.9fr) minmax(120px, 1fr) minmax(56px, 0.45fr)';

const MESSAGE_STATUS_FILTER_OPTIONS = [
  { value: null, label: 'All' },
  ...MESSAGE_DISPLAY_STATUSES.map((status) => ({ value: status, label: status })),
];

const MESSAGE_FLAG_FILTER_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'No Flags', label: 'No Flags' },
  { value: 'Has Flags', label: 'Has Flags' },
];

/** Total Flags count for the parent Message. */
function getFlagsCount(message) {
  if (!message) return 0;
  if (typeof message.flagsCount === 'number') return message.flagsCount;
  if (Array.isArray(message.flags)) return message.flags.length;
  if (message.flagStatus && message.flagStatus !== 'None') return 1;
  return 0;
}

/** Operational Status: Active | Under Review | Blocked | Restricted. */
function getMessageRecordStatus(message) {
  if (!message) return 'Active';
  return normalizeMessageStatus(message.status, message.messagingState, message.messages, {
    underReview: message.underReview,
    caseStatus: message.caseStatus,
    caseSeverity: message.caseSeverity,
  });
}

function getLatestMessageStatus(message) {
  return normalizeMessageDeliveryStatus(message?.latestMessageStatus);
}

function messageStatusTextClass(status) {
  if (status === 'Read') return 'text-freshgreen';
  if (status === 'Delivered') return 'text-primaryblue';
  if (status === 'Sent') return 'text-warmyellow';
  return '';
}

function matchesMessageSearch(message, searchLower) {
  if (!searchLower) return true;

  const messageId = (message.messageId || message.conversationId || message.threadId || '').toLowerCase();
  const customerName = (message.customer?.name || '').toLowerCase();
  const customerId = (message.customer?.id || '').toLowerCase();
  const businessName = (message.business?.name || '').toLowerCase();
  const businessId = (message.business?.id || '').toLowerCase();
  const postTitle = (message.startingPostTitle || message.startedFrom || '').toLowerCase();
  const postId = (message.startingPostId || '').toLowerCase();
  const flagIds = (message.flags || []).map((f) => String(f.flagId || '').toLowerCase());
  const caseId = (message.moderationCaseId || message.caseStatus || '').toString().toLowerCase();
  const caseIds = (message.moderationCases || []).map((c) => String(c.caseId || '').toLowerCase());
  const threadIds = (message.messageThreadIds || []).map((id) => String(id).toLowerCase());

  return (
    messageId.includes(searchLower) ||
    customerName.includes(searchLower) ||
    customerId.includes(searchLower) ||
    businessName.includes(searchLower) ||
    businessId.includes(searchLower) ||
    postTitle.includes(searchLower) ||
    postId.includes(searchLower) ||
    flagIds.some((id) => id.includes(searchLower)) ||
    caseId.includes(searchLower) ||
    caseIds.some((id) => id.includes(searchLower)) ||
    threadIds.some((id) => id.includes(searchLower))
  );
}

const Messages = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({
    status: null, // All | Active | Under Review | Blocked | Restricted
    flag: 'All', // All | No Flags | Has Flags
  });

  const [statusOpen, setStatusOpen] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const statusRef = useRef(null);
  const flagRef = useRef(null);

  useClickOutside(statusRef, () => setStatusOpen(false));
  useClickOutside(flagRef, () => setFlagOpen(false));

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFilteredCount, setTotalFilteredCount] = useState(0);
  const [sortField, setSortField] = useState('lastMessageOn');
  const [sortOrder, setSortOrder] = useState('desc');

  const {
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    rowActionsMenuLayout,
    rowActionsMenuRef,
    rowActionsMenuPlacementReady,
    toggleRowActions,
    closeRowActions,
  } = useInlineRowActions(ROW_ACTIONS_MENU_HEIGHT.SINGLE);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const fetchMessages = async () => {
      setLoading(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 500));
        const allMessages = getMessagesForScenarios();

        let filtered = allMessages;

        if (debouncedSearch) {
          const searchLower = debouncedSearch.toLowerCase().trim();
          filtered = filtered.filter((msg) => matchesMessageSearch(msg, searchLower));
        }

        if (filters.status) {
          filtered = filtered.filter((msg) => getMessageRecordStatus(msg) === filters.status);
        }

        if (filters.flag === 'Has Flags') {
          filtered = filtered.filter((msg) => getFlagsCount(msg) > 0);
        } else if (filters.flag === 'No Flags') {
          filtered = filtered.filter((msg) => getFlagsCount(msg) === 0);
        }

        filtered.sort((a, b) => {
          let aVal;
          let bVal;
          if (sortField === 'lastMessageOn' || sortField === 'lastActivity') {
            aVal = new Date(a.lastMessageOn || a.lastActivity).getTime();
            bVal = new Date(b.lastMessageOn || b.lastActivity).getTime();
          } else if (sortField === 'status') {
            aVal = getMessageRecordStatus(a).toLowerCase();
            bVal = getMessageRecordStatus(b).toLowerCase();
          } else if (sortField === 'customer') {
            aVal = (a.customer?.name || '').toLowerCase();
            bVal = (b.customer?.name || '').toLowerCase();
          } else if (sortField === 'business') {
            aVal = (a.business?.name || '').toLowerCase();
            bVal = (b.business?.name || '').toLowerCase();
          } else {
            aVal = a[sortField];
            bVal = b[sortField];
          }
          if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
          if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
          return 0;
        });

        const total = filtered.length;
        setTotalFilteredCount(total);
        setTotalPages(Math.max(1, Math.ceil(total / CONFIG_LIST_PAGE_SIZE)));
        const startIndex = (currentPage - 1) * CONFIG_LIST_PAGE_SIZE;
        setMessages(filtered.slice(startIndex, startIndex + CONFIG_LIST_PAGE_SIZE));
        setError(null);
      } catch (err) {
        setError('Failed to load messages.');
        setMessages([]);
        setTotalFilteredCount(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [debouncedSearch, filters, currentPage, sortField, sortOrder]);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const formatDateTime = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const openMessageDetail = (message) => {
    navigate(`/admin/communication/all/${message.threadId || message.messageId || message.id}`, {
      state: { from: location.pathname, message },
    });
  };

  const effectivePage = Math.min(currentPage, totalPages);
  const rangeStart =
    totalFilteredCount === 0 ? 0 : (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE + 1;
  const rangeEnd = Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalFilteredCount);
  const showPagination = totalFilteredCount > CONFIG_LIST_PAGE_SIZE;

  return (
    <AdminLayout>
      <div>
        <AdminPageTitle>Messages</AdminPageTitle>

        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <AdminSearchField
              className="w-full max-w-[26.25rem]"
              type="text"
              placeholder="Message ID, Message Thread ID, Customer, Business, Post, Flag, Case"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search messages"
            />

            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Status"
                value={filters.status}
                options={MESSAGE_STATUS_FILTER_OPTIONS}
                onChange={(status) => handleFilterChange('status', status)}
                menuRef={statusRef}
                open={statusOpen}
                setOpen={setStatusOpen}
              />
              <AdminListFilterDropdown
                label="Flags"
                value={filters.flag}
                options={MESSAGE_FLAG_FILTER_OPTIONS}
                onChange={(flag) => handleFilterChange('flag', flag)}
                menuRef={flagRef}
                open={flagOpen}
                setOpen={setFlagOpen}
              />
            </div>
          </div>
        </div>

        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 bg-white animate-pulse rounded" />
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-10">
              <AdminAlertText className="text-alertred mb-4 block">{error}</AdminAlertText>
              <AdminRowAction onClick={() => window.location.reload()}>Retry</AdminRowAction>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-10">
              <AdminSupportingText className="text-mediumgray">No messages match your filters.</AdminSupportingText>
            </div>
          ) : (
            <div ref={tableScrollRef} className="overflow-x-auto">
              <div className="min-w-[1100px]">
                <div
                  className="grid py-3 text-offblack border-b border-lightgray px-4 gap-3"
                  style={{ gridTemplateColumns: MESSAGES_LIST_GRID }}
                >
                  <div className="flex items-center">
                    <AdminTableHeader>Message ID</AdminTableHeader>
                  </div>
                  <div className="flex items-center">
                    <AdminTableHeader>Account</AdminTableHeader>
                  </div>
                  <div className="flex items-center">
                    <AdminTableHeader>Latest</AdminTableHeader>
                  </div>
                  <div className="flex items-center">
                    <AdminTableHeader>Threads</AdminTableHeader>
                  </div>
                  <div className="flex items-center">
                    <AdminTableHeader>Status</AdminTableHeader>
                  </div>
                  <div className="flex items-center">
                    <AdminTableHeader>Flags</AdminTableHeader>
                  </div>
                  <div className="flex items-center">
                    <AdminSortableTableHeader onClick={() => handleSort('status')}>Status</AdminSortableTableHeader>
                  </div>
                  <div className="flex items-center">
                    <AdminSortableTableHeader onClick={() => handleSort('lastMessageOn')}>
                      Last Message On
                    </AdminSortableTableHeader>
                  </div>
                  <div className="flex items-center justify-center">
                    <AdminTableHeader>Action</AdminTableHeader>
                  </div>
                </div>

                {messages.map((message, index) => {
                  const flagsCount = getFlagsCount(message);
                  const recordStatus = getMessageRecordStatus(message);
                  const deliveryStatus = getLatestMessageStatus(message);
                  const messageId = message.messageId || message.conversationId || message.threadId;
                  const rowKey = messageId || `idx-${index}`;

                  return (
                    <div
                      key={rowKey}
                      role="button"
                      tabIndex={0}
                      onClick={() => openMessageDetail(message)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openMessageDetail(message);
                        }
                      }}
                      className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-3 cursor-pointer"
                      style={{ gridTemplateColumns: MESSAGES_LIST_GRID }}
                    >
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={messageId}>
                          {messageId || '—'}
                        </AdminTableBody>
                      </div>

                      <div className="flex items-center gap-2 min-w-0">
                        <AdminRowAvatar
                          src={message.customer?.avatar || require('../../../assets/img/User.png')}
                          alt={message.customer?.name}
                          onError={(e) => {
                            e.target.src = require('../../../assets/img/User.png');
                          }}
                        />
                        <AdminTableBody emphasis className="truncate" title={message.customer?.name}>
                          {message.customer?.name || '—'}
                        </AdminTableBody>
                      </div>

                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={message.latestMessagePreview}>
                          {message.latestMessagePreview || '—'}
                        </AdminTableBody>
                      </div>

                      <div className="flex items-center">
                        <AdminTableBody className="tabular-nums">
                          {message.totalMessageThreads ?? message.messageCount ?? 0}
                        </AdminTableBody>
                      </div>

                      <div className="flex items-center min-w-0">
                        <AdminTableBody className={`truncate ${messageStatusTextClass(deliveryStatus)}`.trim()}>
                          {deliveryStatus}
                        </AdminTableBody>
                      </div>

                      <div className="flex items-center">
                        <AdminTableBody className="tabular-nums">{flagsCount}</AdminTableBody>
                      </div>

                      <div className="flex items-center min-w-0">
                        <DashboardStatusLabel
                          className={`whitespace-nowrap ${messageThreadStatusBadgeClass(recordStatus)}`}
                        >
                          {recordStatus}
                        </DashboardStatusLabel>
                      </div>

                      <div className="flex items-center min-w-0">
                        <AdminTableMeta>
                          {formatDateTime(message.lastMessageOn || message.lastActivity)}
                        </AdminTableMeta>
                      </div>

                      <div className="flex items-center justify-center shrink-0 overflow-visible" onClick={(e) => e.stopPropagation()}>
                        <div
                          className="relative inline-flex"
                          ref={(el) => {
                            rowActionsRefs.current[rowKey] = el;
                          }}
                        >
                          <AdminRowActionsButton
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRowActions(rowKey);
                            }}
                            aria-expanded={!!rowActionsOpen[rowKey]}
                            title="Actions"
                            aria-label="Row actions"
                          >
                            <AdminRowMenuIcon />
                          </AdminRowActionsButton>
                          {rowActionsOpen[rowKey] && rowActionsMenuLayout && (
                            <AdminFixedRowActionsMenu
                              layout={rowActionsMenuLayout}
                              menuRef={rowActionsMenuRef}
                              placementReady={rowActionsMenuPlacementReady}
                            >
                              <AdminRowMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeRowActions();
                                  openMessageDetail(message);
                                }}
                              >
                                View
                              </AdminRowMenuItem>
                            </AdminFixedRowActionsMenu>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={!loading && !error && showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalFilteredCount}
          currentPage={effectivePage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default Messages;
